import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

export interface ConfirmModalData<T = any> {
  heading: string;
  detail?: string;
  itemName?: string;
  btnText: string;
  cancelText?: string;
  btnType?: 'delete' | 'primary' | 'warning';
  icon?: string;
  returnData?: T;
}

@Component({
  selector: 'app-confirm-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if(modalData){
    <div class="space-y-6 animate-in fade-in zoom-in duration-300 relative">
      <!-- Close Button -->
      <button class="btn btn-ghost btn-circle btn-sm absolute -top-4 -right-4 h-8 w-8 min-h-0 bg-base-100 shadow-sm border border-white/5" (click)="cancel()">
        <i class="fa-solid fa-times opacity-50"></i>
      </button>

      <!-- Icon & Heading -->
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-lg transition-transform hover:scale-105"
             [ngClass]="{
               'bg-error/10 text-error': modalData.btnType === 'delete',
               'bg-primary/10 text-primary': modalData.btnType === 'primary',
               'bg-warning/10 text-warning': modalData.btnType === 'warning'
             }">
          <i class="fa-solid" [class]="modalData.icon || (modalData.btnType === 'delete' ? 'fa-trash-can' : 'fa-circle-check')"></i>
        </div>
        <div>
          <h3 class="text-xl font-black tracking-tight">{{ modalData.heading }}</h3>
          @if (modalData.itemName) {
            <p class="text-xs font-mono opacity-40 uppercase tracking-widest mt-0.5">{{ modalData.itemName }}</p>
          }
        </div>
      </div>

      <!-- Detail -->
      <div class="bg-base-200/50 rounded-2xl p-5 border border-white/5">
        <p class="text-sm font-medium leading-relaxed opacity-70">
          {{ modalData.detail || 'Are you sure you want to proceed with this action?' }}
        </p>
      </div>

      <!-- Actions -->
      <div class="flex gap-3 pt-2">
        <button class="btn btn-ghost flex-1 rounded-xl font-bold uppercase text-[10px] tracking-widest transition-all hover:bg-base-200" 
                (click)="cancel()">
          {{ modalData.cancelText || 'Cancel' }}
        </button>

        <button
          class="btn flex-[1.5] rounded-xl font-black uppercase text-[10px] tracking-widest shadow-lg transition-all hover:scale-[1.02] active:scale-95"
          [ngClass]="{
            'btn-error shadow-error/20': modalData.btnType === 'delete',
            'btn-primary shadow-primary/20': modalData.btnType === 'primary',
            'btn-warning shadow-warning/20': modalData.btnType === 'warning'
          }"
          (click)="confirm()"
        >
          {{ modalData.btnText }}
        </button>
      </div>
    </div>
    }
  `,
})
export class ConfirmModalComponent {
  @Input() modalData!: ConfirmModalData<any>;
  closeWithResult!: (result: any) => void;

  confirm() {
    // send back either custom returnData or true
    this.closeWithResult(this.modalData.returnData ?? true);
  }

  cancel() {
    this.closeWithResult(null); // or false if you prefer
  }
}
